import { Component } from '@theme/component';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];
  index = 0;
  scale = 1;
  x = 0;
  y = 0;
  pointers = new Map();

  connectedCallback() {
    super.connectedCallback();
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.stage = this.querySelector('.dialog-zoomed-gallery');
    this.stage.addEventListener('wheel', this.wheel, { ...options, passive: false });
    this.stage.addEventListener('pointerdown', this.pointerDown, options);
    this.stage.addEventListener('pointermove', this.pointerMove, options);
    this.stage.addEventListener('pointerup', this.pointerUp, options);
    this.stage.addEventListener('pointercancel', this.pointerUp, options);
    this.refs.dialog.addEventListener('cancel', this.cancel, options);
    this.refs.dialog.addEventListener('close', this.restore, options);
  }

  disconnectedCallback() {
    this.controller?.abort();
    if (this.refs.dialog?.open) { this.close(); this.restore(); }
    super.disconnectedCallback();
  }

  open(index, event) {
    event?.preventDefault();
    this.opener = document.activeElement;
    this.previousOverflow = document.documentElement.style.overflow;
    document.documentElement.style.overflow = 'hidden';
    if (!this.refs.dialog.open) this.refs.dialog.showModal();
    this.selectThumbnail(Number(index));
    this.refs.dialog.querySelector('.dialog-zoomed-gallery__close-button')?.focus();
  }

  close() {
    this.refs.dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  cancel = (event) => { event.preventDefault(); this.close(); };
  restore = () => {
    document.documentElement.style.overflow = this.previousOverflow ?? '';
    this.opener?.focus({ preventScroll: true });
    this.pointers.clear();
  };

  handleKeyDown(event) {
    if (event.key === 'Escape') { event.preventDefault(); this.close(); }
    if (event.key === 'ArrowRight') { event.preventDefault(); this.next(); }
    if (event.key === 'ArrowLeft') { event.preventDefault(); this.previous(); }
    if (event.key === '+' || event.key === '=') { event.preventDefault(); this.zoomIn(); }
    if (event.key === '-') { event.preventDefault(); this.zoomOut(); }
    if (event.key === '0') { event.preventDefault(); this.resetZoom(); }
  }

  previous() { this.selectThumbnail((this.index - 1 + this.refs.media.length) % this.refs.media.length); }
  next() { this.selectThumbnail((this.index + 1) % this.refs.media.length); }
  handleThumbnailClick(index) { this.selectThumbnail(Number(index)); }
  handleThumbnailPointerEnter(index) { this.loadHighResolutionImage(this.refs.media[Number(index)]); }

  selectThumbnail(index) {
    if (!Number.isInteger(index) || !this.refs.media[index]) return;
    for (const element of this.refs.media) {
      element.querySelectorAll('video').forEach(video => video.pause());
      element.querySelectorAll('iframe').forEach(frame => { if (frame.src) frame.src = frame.src; });
    }
    this.index = index;
    this.pointers.clear();
    this.refs.media.forEach((element, i) => { element.hidden = i !== index; });
    this.refs.thumbnails.querySelectorAll('button').forEach((button, i) => {
      button.setAttribute('aria-pressed', String(i === index));
      button.removeAttribute('aria-selected');
    });
    const thumbnail = this.refs.thumbnails.children[index];
    if (thumbnail) this.refs.thumbnails.scrollLeft = thumbnail.offsetLeft - this.refs.thumbnails.clientWidth / 2 + thumbnail.clientWidth / 2;
    this.refs.dialog.scrollTop = 0;
    this.resetZoom();
    this.loadHighResolutionImage(this.refs.media[index]);
    const count = this.querySelector('[data-viewer-count]');
    if (count) count.textContent = `${index + 1} / ${this.refs.media.length}`;
    this.querySelectorAll('[data-image-zoom]').forEach(button => {
      button.disabled = !this.refs.media[index].classList.contains('product-media-container--image');
    });
    this.querySelectorAll('[data-image-navigation]').forEach(button => { button.hidden = this.refs.media.length < 2; });
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  loadHighResolutionImage(container) {
    const image = container?.querySelector('img.product-media__image');
    const url = image?.getAttribute('data_max_resolution');
    if (!url || image.dataset.hbHighResolution) return;
    image.dataset.hbHighResolution = 'true';
    image.removeAttribute('srcset');
    image.src = url;
  }

  resetZoom() { this.scale = 1; this.x = 0; this.y = 0; this.paint(); }
  zoomIn() { this.zoomTo(this.scale * 1.4); }
  zoomOut() { this.zoomTo(this.scale / 1.4); }
  zoomTo(value, clientX, clientY) {
    if (!this.refs.media[this.index]?.classList.contains('product-media-container--image')) return;
    const rect = this.stage.getBoundingClientRect();
    const scale = Math.min(4, Math.max(1, value));
    const px = (clientX ?? rect.left + rect.width / 2) - rect.left - rect.width / 2;
    const py = (clientY ?? rect.top + rect.height / 2) - rect.top - rect.height / 2;
    this.x = px - (px - this.x) * scale / this.scale;
    this.y = py - (py - this.y) * scale / this.scale;
    this.scale = scale;
    if (scale === 1) { this.x = 0; this.y = 0; }
    this.paint();
  }

  paint() {
    const rect = this.stage.getBoundingClientRect();
    const maxX = rect.width * (this.scale - 1) / 2;
    const maxY = rect.height * (this.scale - 1) / 2;
    this.x = Math.min(maxX, Math.max(-maxX, this.x));
    this.y = Math.min(maxY, Math.max(-maxY, this.y));
    this.refs.media.forEach((element, i) => {
      const image = element.querySelector('.product-media__image');
      if (image) image.style.transform = i === this.index ? `translate(${this.x}px, ${this.y}px) scale(${this.scale})` : '';
    });
    this.stage.dataset.zoomed = String(this.scale > 1);
    const reset = this.querySelector('[data-zoom-level]');
    if (reset) reset.textContent = `${Math.round(this.scale * 100)}%`;
  }

  wheel = (event) => {
    if (!this.refs.media[this.index]?.classList.contains('product-media-container--image')) return;
    event.preventDefault();
    const delta = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? this.stage.clientHeight : 1);
    this.zoomTo(this.scale * Math.exp(-delta * 0.002), event.clientX, event.clientY);
  };
  pointerDown = (event) => {
    if (!this.refs.media[this.index]?.classList.contains('product-media-container--image')) return;
    this.stage.setPointerCapture(event.pointerId);
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (this.pointers.size === 2) {
      const [a, b] = [...this.pointers.values()];
      this.pinchDistance = Math.hypot(a.x - b.x, a.y - b.y);
    }
  };
  pointerMove = (event) => {
    const previous = this.pointers.get(event.pointerId);
    if (!previous) return;
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (this.pointers.size === 2) {
      const [a, b] = [...this.pointers.values()];
      const distance = Math.hypot(a.x - b.x, a.y - b.y);
      if (this.pinchDistance > 0) this.zoomTo(this.scale * distance / this.pinchDistance, (a.x + b.x) / 2, (a.y + b.y) / 2);
      this.pinchDistance = distance;
    } else if (this.scale > 1) {
      this.x += event.clientX - previous.x;
      this.y += event.clientY - previous.y;
      this.paint();
    }
  };
  pointerUp = (event) => { this.pointers.delete(event.pointerId); this.pinchDistance = 0; };
}

if (!customElements.get('zoom-dialog')) customElements.define('zoom-dialog', ZoomDialog);
